import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Award, MapPin } from "lucide-react";
import JourneyMap from "./JourneyMap";
import { MaskLines } from "./Motion";
import { ERAS, IMG } from "../../data/content";

export default function Journey() {
  const [i, setI] = useState(0);
  const era = ERAS[i];
  const go = (d) => setI((v) => (v + d + ERAS.length) % ERAS.length);

  return (
    <section id="journey" className="relative bg-forest py-24 text-white lg:py-36" data-testid="journey-section">
      <div className="wrap">
        <div className="flex flex-col justify-between gap-8 lg:flex-row lg:items-end">
          <div>
            <p className="eyebrow text-white">Chapter 04 — The Journey</p>
            <MaskLines className="mt-6 font-display text-5xl font-semibold leading-[0.98] sm:text-6xl lg:text-7xl" lines={["From a Yorkshire pub", <span key="b" className="italic text-mist">to the Himalayas & back.</span>]} />
          </div>
          <div className="flex gap-3">
            <button data-testid="journey-prev-btn" onClick={() => go(-1)} aria-label="Previous era" className="grid h-14 w-14 place-items-center rounded-full border border-white/50 transition-colors hover:border-white hover:bg-white/10"><ArrowLeft className="h-5 w-5" /></button>
            <button data-testid="journey-next-btn" onClick={() => go(1)} aria-label="Next era" className="grid h-14 w-14 place-items-center rounded-full bg-gold text-forest-deep transition-transform hover:scale-105"><ArrowRight className="h-5 w-5" /></button>
          </div>
        </div>

        <div className="mt-12 flex gap-2 overflow-x-auto border-b border-white/30 pb-px" role="tablist">
          {ERAS.map((e, k) => (
            <button key={e.id} role="tab" aria-selected={k === i} data-testid={`journey-tab-${e.id}`} onClick={() => setI(k)} className={`relative shrink-0 px-4 pb-4 pt-2 text-left transition-colors ${k === i ? "text-white" : "text-white/90 hover:text-white"}`}>
              <span className="block font-mono text-xl sm:text-2xl">{e.years}</span>
              <span className="mt-1 block text-xs uppercase tracking-[0.16em]">{e.place}</span>
              {k === i && <motion.span layoutId="era-bar" className="absolute inset-x-0 -bottom-px h-[3px] bg-gold" />}
            </button>
          ))}
        </div>

        <div className="mt-10 grid grid-cols-1 gap-8 lg:grid-cols-12">
          <div className="relative aspect-[700/360] overflow-hidden rounded-[1.5rem] border border-white/20 bg-white/10 lg:col-span-7">
            <JourneyMap focus={era.focus} />
          </div>
          <div className="grid lg:col-span-5 [grid-template-areas:'stack']">
            {ERAS.map((e, k) => (
              <motion.article
                key={e.id}
                data-testid={`journey-card-${e.id}`}
                aria-hidden={k !== i}
                initial={false}
                animate={{ opacity: k === i ? 1 : 0, y: k === i ? 0 : 20 }}
                transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                style={{ pointerEvents: k === i ? "auto" : "none" }}
                className="flex flex-col [grid-area:stack]"
              >
                <div className="relative aspect-[16/9] overflow-hidden rounded-[1.25rem]">
                  <img src={IMG(e.photo)} alt={e.title} className="h-full w-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-forest/85 to-transparent" />
                  <span className="absolute bottom-4 left-4 font-mono text-[12px] uppercase tracking-[0.2em] text-white">{e.years}</span>
                  {e.award && (
                    <span className="absolute right-4 top-4 flex items-center gap-2 rounded-full bg-gold px-3 py-1.5 text-xs font-medium text-forest-deep"><Award className="h-4 w-4" /> Best Cider Award · USA</span>
                  )}
                </div>
                <h3 className="mt-6 font-display text-3xl font-semibold leading-tight">{e.title}</h3>
                <p className="mt-3 text-base leading-relaxed text-white">{e.text}</p>
                <div className="mt-5 flex flex-wrap gap-2">
                  {e.cities.map((c) => (
                    <span key={c} className="inline-flex items-center gap-1.5 rounded-full border border-white/40 px-3 py-1.5 text-xs text-white"><MapPin className="h-3 w-3 text-white" />{c}</span>
                  ))}
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
